import { Loader, ImageOff } from 'lucide-react';
import { useEventMediaUrl } from '../../hooks/useEventMediaUrl';
import type { EventMedia } from '../../types/eventTeam';
import { cn } from '../../utils/cn';

export interface EventMediaThumbnailProps {
  media: EventMedia;
  className?: string;
  imgClassName?: string;
  onClick?: () => void;
}

/** Authenticated event image thumbnail (blob URL via `useEventMediaUrl`). */
export function EventMediaThumbnail({ media, className, imgClassName, onClick }: EventMediaThumbnailProps) {
  const { url, loading, error } = useEventMediaUrl(media.id);

  const inner = loading ? (
    <div className="flex items-center justify-center w-full h-full min-h-[80px] bg-glass-200/10">
      <Loader className="w-5 h-5 animate-spin text-glass-400" />
    </div>
  ) : error || !url ? (
    <div className="flex flex-col items-center justify-center gap-1 w-full h-full min-h-[80px] bg-glass-200/10 text-glass-500 text-xs px-2 text-center">
      <ImageOff className="w-5 h-5" />
      {error ? 'Failed to load' : 'No preview'}
    </div>
  ) : (
    <img
      src={url}
      alt={media.caption || media.filename}
      className={cn('w-full h-full object-cover', imgClassName)}
      loading="lazy"
    />
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'rounded-lg overflow-hidden border border-glass-200/20 bg-glass-200/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/50',
          className,
        )}
      >
        {inner}
      </button>
    );
  }

  return (
    <div className={cn('rounded-lg overflow-hidden border border-glass-200/20 bg-glass-200/5', className)}>
      {inner}
    </div>
  );
}
